import { styled } from '@sds-system/system';
import { getAllStyledProps, processNestedCSS } from '@sds-system/utils';
import type {
  StyledTextareaInputProps,
  StyledTextareaRootProps,
  StyledTextareaWrapperProps,
} from './Textarea.types';

export const TextareaRoot = styled('div', {
  componentName: 'Textarea',
  slot: 'root',
})<StyledTextareaRootProps>(({ theme }) => {
  return {
    display: 'flex',
    width: '100%',
    height: 'max-content',
    flexDirection: 'column',
    gap: theme.space[8],
  };
}, getAllStyledProps);

export const TextareaWrapper = styled('div', {
  componentName: 'Textarea',
  slot: 'wrapper',
})<StyledTextareaWrapperProps>(
  ({ theme, disabled, isError, errorMessages, isSuccess, successMessages }) => {
    return {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: 208,
      padding: theme.space[16],
      border: `1px solid ${theme.palette.border.secondary}`,
      borderRadius: theme.borderRadius[8],
      gap: theme.space[8],

      ...(disabled && {
        borderColor: theme.palette.border.disabled,
        backgroundColor: theme.palette.bg.disabledLight,
        color: theme.palette.text.secondary,
        '::placeholder': processNestedCSS(
          {
            color: theme.palette.text.disabled,
          },
          theme,
        ),
      }),
      ...((isSuccess || (successMessages?.length || 0) > 0) && {
        borderColor: theme.palette.border.success,
      }),
      ':focus-within': processNestedCSS(
        !isError && (errorMessages?.length || 0) <= 0
          ? {
              outline: 'none',
              borderColor: theme.palette.border.interactive.primary,
            }
          : {},
        theme,
      ),
      ...((isError || (errorMessages?.length || 0) > 0) && {
        borderColor: theme.palette.border.error,
      }),
    };
  },
  getAllStyledProps,
);

export const TextareaInput = styled('textarea', {
  componentName: 'Textarea',
  slot: 'input',
})<StyledTextareaInputProps>(({ theme, disabled }) => {
  return {
    width: '100%',
    height: '100%',
    padding: 0,
    fontFamily: 'inherit',
    resize: 'none',
    border: 'none',
    outline: 'none',

    ...theme.typography.body2.regular,

    ...(disabled && {
      borderColor: theme.palette.border.disabled,
      backgroundColor: theme.palette.bg.disabledLight,
      color: theme.palette.text.secondary,
      '::placeholder': processNestedCSS(
        {
          color: theme.palette.text.disabled,
        },
        theme,
      ),
    }),

    '::placeholder': {
      color: theme.palette.text.tertiary,
    },
  };
}, getAllStyledProps);
